import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { SectionHeader } from '@/components/molecules/SectionHeader';
import { dwrClassesById } from '@/data/classes';
import type { Character, MoveRef } from '@/schemas/character';
import type { ClassDefinition } from '@/schemas/classDefinition';
import type { ClassId } from '@/schemas/classId';
import { hasMoveSetup, type Move } from '@/schemas/move';
import { getBundleMovesToApply } from '@/services/bundle';
import { getMulticlassClassPool, getMulticlassMovePool } from '@/services/multiclass';
import { useExpandedState } from '@/hooks/useExpandedState';
import { MoveSetupPanel } from '../../sections/MoveSetupPanel';
import { updateMoveChoiceGroup, updateMoveNarrativeField } from '../../services/moveRefMutations';
import { BundleSetupsSection } from './BundleSetupsSection';
import { GrantedOfTargetSection } from './GrantedOfTargetSection';
import { ClassSelector, MoveList } from './LevelUpStepMulticlassParts';
import type { LevelUpMulticlassPickDraft } from './types';

// page-specific (Fase 2 PR 9): step «Multiclasse» del flusso di salita
// di livello (D7 — FI-17). Pixel-parity con pattern shell PR 8:
// <SectionHeader> + body card-driven, no bottoni interni (delegati a
// <StepNav> esterno renderizzato da LevelUpFlowPage).
//
// Visibile solo se la mossa appena scelta ha `MoveSchema.multiclass`.
// UX coerente con `LevelUpStepMove`:
//
//  1. **Classe-source**: `<Select>` con il pool computato da
//     `getMulticlassClassPool` (filtri picks + extends-by-archetype +
//     extends-by-move + excludes). Auto-select su pool=1.
//  2. **Mossa-target**: lista di `<MoveCard>` (radio + chevron)
//     computata da `getMulticlassMovePool` (filtri tags `'multiclasse'`
//     esclusi + tipo + owned + prereq + livello effettivo).
//  3. **Setup mossa-target** (opzionale): se la mossa selezionata ha
//     `hasMoveSetup`, `<NestedMoveSetup>` attiva il `MoveSetupEditor`.
//  4. **Bundle setups** (D7-bundle): `<BundleSetupsSection>` per le
//     mosse del bundle che richiedono setup interattivo.
//
// **PR 9 cambio API**: prop `onNext` / `onBack` rimosse.
// Sub-componenti `ClassSelector` / `MoveList` / `NestedMoveSetup` +
// helper `describeMulticlass` estratti in `LevelUpStepMulticlassParts.tsx`
// per rispettare `max-lines: 400`.

interface LevelUpStepMulticlassProps {
  character: Character;
  classDef: ClassDefinition;
  moveDef: Move;
  pick: LevelUpMulticlassPickDraft | undefined;
  onPickChange: (pick: LevelUpMulticlassPickDraft | undefined) => void;
}

export function LevelUpStepMulticlass(props: LevelUpStepMulticlassProps) {
  const { character, classDef, moveDef, pick, onPickChange } = props;
  const config = moveDef.multiclass;

  const classPool = useMemo<readonly ClassId[]>(() => {
    if (!config) return [];
    return getMulticlassClassPool(character, config, classDef);
  }, [character, config, classDef]);

  // Auto-select su classPool=1
  useEffect(() => {
    const onlyClass = classPool[0];
    if (classPool.length === 1 && onlyClass !== undefined && pick === undefined) {
      onPickChange({ sourceClassId: onlyClass, selectedMoveId: '' });
    }
  }, [classPool, pick, onPickChange]);

  const movePool = useMemo<readonly Move[]>(() => {
    if (!config || pick === undefined) return [];
    return getMulticlassMovePool(character, pick.sourceClassId, config, classDef);
  }, [character, config, classDef, pick]);

  const moveIds = useMemo(() => movePool.map((m) => m.id), [movePool]);
  const expandState = useExpandedState(moveIds);

  const selectedMove = movePool.find((m) => m.id === pick?.selectedMoveId);

  // D7-bundle (FI-17 follow-up): mosse del bundle che richiedono setup
  // interattivo. Filtrate via `getBundleMovesToApply` + `hasMoveSetup`.
  const bundleMovesNeedingSetup = useMemo<readonly Move[]>(() => {
    if (!selectedMove) return [];
    const toApply = getBundleMovesToApply(selectedMove, character);
    return toApply.filter((m) => hasMoveSetup(m));
  }, [selectedMove, character]);

  // FI-75: setup pannello inline alla MoveCard della mossa multiclasse
  // selezionata. Visibile solo per la mossa target del pick corrente.
  // FI-79: ownedMoveIds del PG per disabilitare spell-options con
  // `requiresMove` non soddisfatto. Coerente con builder L1 + LevelUpStepMove.
  const ownedMoveIds = useMemo(() => character.moves.map((m) => m.id), [character.moves]);

  const renderSetupSlot = useCallback(
    (move: Move): ReactNode => {
      if (pick?.selectedMoveId !== move.id || !hasMoveSetup(move)) return null;
      const setup = pick.selectedMoveSetup;
      const moveRef: MoveRef = setup
        ? { id: move.id, ...setup }
        : { id: move.id, selectedChoices: [] };
      const onSetupChange = (next: Omit<MoveRef, 'id'>): void => {
        onPickChange({ ...pick, selectedMoveSetup: next });
      };
      return (
        <MoveSetupPanel
          move={move}
          selectedByGroup={buildSelectedByGroup(move, moveRef)}
          narrativeValues={moveRef.narrativeFieldValues ?? {}}
          recruitOutcome={moveRef.recruitOutcome}
          archetypeId={character.archetypeId}
          ownedMoveIds={ownedMoveIds}
          onChoiceGroupChange={(moveId, groupId, selectedIds) => {
            onSetupChange(
              extractSetup(updateMoveChoiceGroup(moveRef, moveId, groupId, selectedIds)),
            );
          }}
          onNarrativeFieldChange={(moveId, fieldId, value) => {
            onSetupChange(extractSetup(updateMoveNarrativeField(moveRef, moveId, fieldId, value)));
          }}
        />
      );
    },
    [pick, character.archetypeId, ownedMoveIds, onPickChange],
  );

  if (!config) return null;

  return (
    <section>
      <SectionHeader
        title={`Mossa multiclasse da «${moveDef.name}»`}
        sub={describeMulticlass(moveDef, config)}
      />
      <div className="mt-4 space-y-4">
        <ClassSelector
          pool={classPool}
          sourceClassId={pick?.sourceClassId}
          onChange={(sourceClassId) => onPickChange({ sourceClassId, selectedMoveId: '' })}
        />
        {pick !== undefined && (
          <MoveList
            character={character}
            config={config}
            classDef={classDef}
            sourceClassId={pick.sourceClassId}
            movePool={movePool}
            selectedMoveId={pick.selectedMoveId}
            expandState={expandState}
            onSelect={(move) =>
              onPickChange({ sourceClassId: pick.sourceClassId, selectedMoveId: move.id })
            }
            renderSetupSlot={renderSetupSlot}
          />
        )}
        <NestedSetupSections
          character={character}
          selectedMove={selectedMove}
          bundleMovesNeedingSetup={bundleMovesNeedingSetup}
          pick={pick}
          onPickChange={onPickChange}
        />
      </div>
    </section>
  );
}

interface NestedSetupSectionsProps {
  character: Character;
  selectedMove: Move | undefined;
  bundleMovesNeedingSetup: readonly Move[];
  pick: LevelUpMulticlassPickDraft | undefined;
  onPickChange: (pick: LevelUpMulticlassPickDraft | undefined) => void;
}

/**
 * **FI-81 #2 (2026-05-24)** — Estratto da `LevelUpStepMulticlass` per
 * rispettare `max-lines-per-function: 100`. Raccoglie le 2 sezioni
 * nested setup (bundle + granted-of-target) per coerenza didattica.
 */
function NestedSetupSections({
  character,
  selectedMove,
  bundleMovesNeedingSetup,
  pick,
  onPickChange,
}: NestedSetupSectionsProps) {
  return (
    <>
      <BundleSetupsSection
        character={character}
        anchorMove={selectedMove}
        bundleMovesNeedingSetup={bundleMovesNeedingSetup}
        bundleSetups={pick?.bundleSetups ?? {}}
        onBundleSetupsChange={(newBundleSetups) => {
          if (pick === undefined) return;
          onPickChange({ ...pick, bundleSetups: newBundleSetups });
        }}
      />
      <GrantedOfTargetSection
        character={character}
        selectedTargetMove={selectedMove}
        targetSelectedChoiceId={pick?.selectedMoveSetup?.selectedChoices?.[0]}
        grantedChain={pick?.grantedChain ?? []}
        onGrantedChainChange={(nextChain) => {
          if (pick === undefined) return;
          if (nextChain.length === 0) {
            const { grantedChain: _drop, ...rest } = pick;
            onPickChange(rest);
          } else {
            onPickChange({ ...pick, grantedChain: nextChain });
          }
        }}
      />
    </>
  );
}

/**
 * Proietta un `MoveRef` in `selectedByGroup` per `MoveSetupPanel`.
 * Stesso pattern di `LevelUpStepMove.buildSelectedByGroup`.
 */
function buildSelectedByGroup(move: Move, moveRef: MoveRef): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  if (move.choices) result.main = [...moveRef.selectedChoices];
  if (moveRef.selectedChoiceGroups) {
    for (const [groupId, ids] of Object.entries(moveRef.selectedChoiceGroups)) {
      result[groupId] = [...ids];
    }
  }
  return result;
}

function extractSetup(moveRef: MoveRef): Omit<MoveRef, 'id'> {
  const setup: Omit<MoveRef, 'id'> = { selectedChoices: moveRef.selectedChoices };
  if (moveRef.selectedChoiceGroups) setup.selectedChoiceGroups = moveRef.selectedChoiceGroups;
  if (moveRef.narrativeFieldValues) setup.narrativeFieldValues = moveRef.narrativeFieldValues;
  return setup;
}

/** Descrizione user-facing della meccanica multiclasse della mossa. */
function describeMulticlass(moveDef: Move, config: NonNullable<Move['multiclass']>): string {
  if (config.picks.kind === 'any-other-class') {
    const note =
      config.levelAdjustment?.delta === -1
        ? ' (considera te di un livello inferiore ai fini della scelta)'
        : '';
    return `«${moveDef.name}» permette di pescare una mossa non multiclasse da qualunque altra classe${note}.`;
  }
  if (config.picks.kind === 'from-extensions-only') {
    return `«${moveDef.name}» permette di pescare una mossa non multiclasse da un pool che dipende dallo stato del personaggio (mosse possedute e archetipo).`;
  }
  const classes = config.picks.classes.map(classLabel).join(', ');
  return `«${moveDef.name}» permette di pescare una mossa non multiclasse da: ${classes}.`;
}

function classLabel(classId: ClassId): string {
  const def = dwrClassesById[classId];
  return def?.name ?? classId;
}
